import { FetchPolicy, TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { useCardsSearchClient } from 'hooks/search/useCardsSearchClient';

import type {
  UserCardsSearchFacetValuesQuery,
  UserCardsSearchFacetValuesQueryVariables,
  UserCardsSearchQuery,
  UserCardsSearchQueryVariables,
} from './__generated__/useUserCardsSearchClient.graphql';

const USER_CARDS_SEARCH_FACET_VALUES_QUERY = gql`
  query UserCardsSearchFacetValuesQuery(
    $userSlug: String!
    $rarity: Rarity
    $sport: Sport!
    $field: String!
    $facetQuery: String!
    $query: String!
    $limit: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]!
  ) {
    user(slug: $userSlug) {
      slug
      searchCardFacetValues(
        field: $field
        facetQuery: $facetQuery
        query: $query
        limit: $limit
        rarity: $rarity
        sport: $sport
        advancedFilters: $advancedFilters
        refinements: $refinements
      ) {
        ...useCardsSearchClient_searchCardFacetValues
      }
    }
  }
  ${useCardsSearchClient.fragments.searchCardFacetValues}
` as TypedDocumentNode<
  UserCardsSearchFacetValuesQuery,
  UserCardsSearchFacetValuesQueryVariables
>;

const USER_CARDS_SEARCH_QUERY = gql`
  query UserCardsSearchQuery(
    $userSlug: String!
    $rarity: Rarity
    $sport: Sport!
    $query: String!
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $sorts: [SearchSortInput!]!
    $refinements: [SearchRefinementInput!]!
    $facets: [SearchFacetDefinitionInput!]!
  ) {
    user(slug: $userSlug) {
      slug
      searchCards(
        query: $query
        page: $page
        pageSize: $pageSize
        sorts: $sorts
        sport: $sport
        rarity: $rarity
        advancedFilters: $advancedFilters
        refinements: $refinements
        facets: $facets
      ) {
        hits {
          slug
        }
        ...useCardsSearchClient_searchCards
      }
    }
  }
  ${useCardsSearchClient.fragments.searchCards}
` as TypedDocumentNode<UserCardsSearchQuery, UserCardsSearchQueryVariables>;

export const useUserCardsSearchClient = (
  userSlug: string,
  {
    rarity,
    sport,
    sealed,
  }: {
    rarity: Rarity | undefined;
    sport: Sport;
    sealed?: boolean;
    fetchPolicy?: FetchPolicy;
  }
) => {
  const {
    flags: { useNetworkOnlySearchClient = true },
  } = useFeatureFlags();

  const options = useMemo(
    () => ({
      userSlug,
      rarity,
      sport,
      sealed,
      fetchPolicy: useNetworkOnlySearchClient
        ? ('network-only' as const)
        : undefined,
    }),
    [userSlug, rarity, sport, sealed, useNetworkOnlySearchClient]
  );

  return useCardsSearchClient<
    typeof USER_CARDS_SEARCH_QUERY,
    typeof USER_CARDS_SEARCH_FACET_VALUES_QUERY
  >(USER_CARDS_SEARCH_QUERY, USER_CARDS_SEARCH_FACET_VALUES_QUERY, options);
};
